import React from 'react';
import { Repeat } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  /** Header label above the title — e.g. "Routine proposal" or "Update proposal" */
  kicker: string;
  title: string;
  subtitle?: string;
  /** Optional banner above the body — used by the conflict warning variant. */
  banner?: React.ReactNode;
  /** Stacked content sections inside the card body. */
  children: React.ReactNode;
  /** Footer action buttons (Dismiss / Edit / Approve etc.). */
  actions: React.ReactNode;
  /** Whether the card has been resolved (approved/dismissed/etc.) — fades the body and replaces actions. */
  resolved?: { tone: 'success' | 'muted' | 'danger'; label: string; action?: React.ReactNode };
  /** Color accent on the kicker bar; defaults to brand cyan. */
  accentClass?: string;
  /** Icon shown in the header chip; defaults to Repeat (routine cards). */
  icon?: React.ComponentType<{ className?: string }>;
}

/**
 * ProposalCardShell — common chrome for the three routine proposal cards.
 * Keeps the "Approve / Edit / Dismiss" interaction pattern and the
 * resolved-state handling in one place so the variant components stay
 * focused on their content (proposal body, diff, deletion details).
 */
export const ProposalCardShell: React.FC<Props> = ({
  kicker,
  title,
  subtitle,
  banner,
  children,
  actions,
  resolved,
  accentClass = 'bg-primary',
  icon: Icon = Repeat,
}) => {
  const resolvedToneClasses: Record<NonNullable<Props['resolved']>['tone'], string> = {
    success: 'text-success bg-success/15 border-success/20',
    muted: 'text-muted-foreground bg-muted dark:bg-white/5 border-border',
    danger: 'text-destructive bg-destructive/15 border-destructive/20',
  };

  return (
    <div className="rounded-2xl border border-border bg-white dark:bg-white/[0.03] overflow-hidden shadow-sm max-w-xl">
      <div className={clsx('h-1', accentClass)} />
      <div className={clsx('p-5 space-y-4', resolved && 'opacity-80')}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium uppercase tracking-wide text-primary">
              {kicker}
            </div>
            <h3 className="text-base font-semibold text-foreground mt-0.5 break-words">
              {title}
            </h3>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>

        {banner}

        <div className="space-y-3">{children}</div>

        {resolved ? (
          <div className={clsx('rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-between gap-3', resolvedToneClasses[resolved.tone])}>
            <span>{resolved.label}</span>
            {resolved.action}
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalCardShell;
